/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const titleRowStyles = cssMap({
	fixedHeight: {
		minHeight: '24px',
	},
	clickableTitle: {
		cursor: 'pointer',
		backgroundColor: 'transparent',
		textAlign: 'left',
		padding: 0,
		margin: 0,
		overflow: 'hidden',
		minWidth: '0px',
	},
	afterExpandIconSlotWrapper: {
		marginLeft: 'auto',
	},
	inlineIconWrapper: {
		// Render icon as inline-flex so it sits right after the last word of the title text.
		display: 'inline-flex',
		verticalAlign: 'middle',
		marginInlineStart: token('space.050'),
	},
	inlineIconWrapperHidden: {
		// Same as inlineIconWrapper but with visibility: hidden to preserve layout space on non-hover.
		display: 'inline-flex',
		verticalAlign: 'middle',
		marginInlineStart: token('space.050'),
		visibility: 'hidden',
	},
});

export const ResearchExpandable = ({
	children,
	title,
	defaultExpanded = true,
	showExpandIcon = true,
	showExpandAtEnd = true,
	onExpandChanged = undefined,
	iconSpacing,
	fixedTitleRowHeight = false,
	showDownRightIcons = false,
	afterExpandIconSlot,
	alignExpandIcon = 'center',
	onExpandClick,
	layout = 'block',
	hideExpandIcon = false,
}: {
	children: ReactNode;
	title: ReactNode;
	defaultExpanded?: boolean;
	showExpandIcon?: boolean;
	showExpandAtEnd?: boolean;
	onExpandChanged?: (expanded: boolean) => void;
	iconSpacing?: React.ComponentProps<typeof IconButton>['spacing'];
	fixedTitleRowHeight?: boolean;
	showDownRightIcons?: boolean;
	/** Optional slot rendered after the expand/collapse icon (e.g. Answer Now button) */
	afterExpandIconSlot?: ReactNode;
	/** Controls vertical alignment of the expand icon relative to the title. Defaults to 'center'. Use 'start' to align to the first line of the title. */
	alignExpandIcon?: 'center' | 'start';
	/** Called only when the user clicks to expand/collapse — not on programmatic state changes. */
	onExpandClick?: (expanded: boolean) => void;
	/**
	 * Controls the layout of the title row:
	 * - 'block' (default): icon is pushed to the far end of the row via flexbox
	 * - 'inline': icon flows right after the last word of the title text
	 */
	layout?: 'block' | 'inline';
	/** When true, the expand icon is hidden (but still occupies space). Only used when layout='inline'. */
	hideExpandIcon?: boolean;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	// Override any user interaction that might have changed the expanded state when the prop changes so we have control over it
	useEffect(() => {
		setIsExpanded(defaultExpanded);
	}, [defaultExpanded]);

	const setIsExpandedValue = useCallback(
		(value: boolean) => {
			setIsExpanded(value);
			onExpandChanged?.(value);
		},
		[setIsExpanded, onExpandChanged],
	);

	const handleExpandClick = useCallback(() => {
		const newValue = !isExpanded;
		setIsExpandedValue(newValue);
		onExpandClick?.(newValue);
	}, [isExpanded, setIsExpandedValue, onExpandClick]);

	const [ExpandedIcon, CollapsedIcon] = useMemo(() => {
		if (showDownRightIcons) {
			return [ChevronDownIcon, ChevronRightIcon];
		}
		// Original behavior: when showExpandAtEnd is true, use up/down; otherwise use down/right
		if (showExpandAtEnd) {
			return [ChevronUpIcon, ChevronDownIcon];
		}
		return [ChevronDownIcon, ChevronRightIcon];
	}, [showExpandAtEnd, showDownRightIcons]);

	return (
		<Box data-expanded={isExpanded} testId="research-expandable">
			{layout === 'inline' ? (
				// Inline layout: icon flows right after the last word of the title text.
				// The Pressable wraps both title and icon so the entire area is clickable.
				<Pressable
					onClick={handleExpandClick}
					aria-expanded={isExpanded}
					aria-label={
						isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)
					}
					xcss={titleRowStyles.clickableTitle}
				>
					{title}
					<Box
						xcss={
							hideExpandIcon
								? titleRowStyles.inlineIconWrapperHidden
								: titleRowStyles.inlineIconWrapper
						}
					>
						<IconButton
							shape="circle"
							appearance="subtle"
							spacing="compact"
							icon={(iconProps) =>
								isExpanded ? (
									<ExpandedIcon {...iconProps} size="small" />
								) : (
									<CollapsedIcon {...iconProps} size="small" />
								)
							}
							label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
						/>
					</Box>
				</Pressable>
			) : (
				// Block layout: icon pushed to the far end of the row via flexbox.
				<Inline
					spread={showExpandAtEnd ? 'space-between' : undefined}
					alignBlock={alignExpandIcon}
					space="space.050"
					xcss={fixedTitleRowHeight ? titleRowStyles.fixedHeight : undefined}
				>
					<Pressable
						onClick={handleExpandClick}
						aria-expanded={isExpanded}
						aria-label={
							isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)
						}
						xcss={titleRowStyles.clickableTitle}
					>
						{title}
					</Pressable>
					{showExpandIcon && (
						<IconButton
							shape="circle"
							appearance="subtle"
							{...(iconSpacing && { spacing: iconSpacing })}
							icon={(iconProps) =>
								isExpanded ? (
									<ExpandedIcon {...iconProps} size="small" />
								) : (
									<CollapsedIcon {...iconProps} size="small" />
								)
							}
							label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
							onClick={handleExpandClick}
						/>
					)}
					{afterExpandIconSlot && (
						<Box xcss={titleRowStyles.afterExpandIconSlotWrapper}>{afterExpandIconSlot}</Box>
					)}
				</Inline>
			)}
			<ExitingPersistence>
				{isExpanded && (
					<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
						{(motionProps) => <div {...motionProps}>{children}</div>}
					</FadeIn>
				)}
			</ExitingPersistence>
		</Box>
	);
};
